import React, {useState} from 'react';
import { Form, Input, Select, Checkbox, DatePicker } from 'antd';
import dayjs from "dayjs";
import {internalApi} from "../../common/axios";
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import {
    DIALOG_KEY,
    messageTypeList,
    MESSAGE_TYPE,
    PLATFORM, CYCLE
} from "../../shared/utils/enum";
import toast from "react-hot-toast";
import {buildUrl} from "../../shared/utils/net";
import {MessageEntity} from "./MessageEntity";
import {CancelButton, DeleteButton, SaveButton} from "../../shared/components/buttons/CustomButtons";

dayjs.extend(utc);
dayjs.extend(timezone);

const MessageEditModal = ({record, handleClose, tblData, dialogRef, handleSetDialog}) => {
    const data = new MessageEntity(record);

    const [rows, setRows] = useState(data);
    const [selectedOptions, setSelectedOptions] = useState(data.topic);
    const [inputFields, setInputFields] = useState(data.body);
    const [isAdTextIncluded, setIsAdTextIncluded] = useState(false);

    const languageList = [...new Set(tblData.filter(option => option.language !== 'all').map(option => option.language))];

    const handleSetRows = (key, value) => {
        if (key === 'messageType' && value !== MESSAGE_TYPE.REPEAT) {
            setRows((prevState) => {
                return new MessageEntity(
                    {
                        ...prevState,
                        [key]: value, 'end': dayjs().format('YYYY-MM-DD HH:mm:ss'),
                        repeatTime: 0,
                        timeType: CYCLE.MINUTE,
                        sendTime: dayjs().format('YYYY-MM-DD HH:mm:ss')
                    }
                );
            });
        } else {
            setRows((prevState) => {
                return new MessageEntity({...prevState, [key]: value});
            });
        }
    }

    const handleSetRepeatTime = (key, value) => {
        switch (key) {
            case 'timeType':
                let repeatTime = rows.repeatTime;

                repeatTime = viewRepeatTime(rows.timeType, rows.repeatTime);
                repeatTime = calculateRepeatTime(value, repeatTime);

                handleSetRows('timeType', value);
                handleSetRows('repeatTime', repeatTime);
                break;
            case 'repeatTime':
                value = calculateRepeatTime(rows.timeType, value);
                handleSetRows('repeatTime', value);
                break;
            default:
                break;
        }
    }

    const viewRepeatTime = (timeType, newRepeatTime) => {
        switch (timeType) {
            case CYCLE.MINUTE:
                return newRepeatTime;
            case CYCLE.HOUR:
                return newRepeatTime / 60;
            case CYCLE.DAY:
                return newRepeatTime / 1440;
            case CYCLE.WEEK:
                return newRepeatTime / 10080;
            case CYCLE.MONTH:
                return newRepeatTime / 43800;
            case CYCLE.YEAR:
                return newRepeatTime / 525600;
            default:
                return newRepeatTime;
        }
    }

    const calculateRepeatTime = (timeType, repeatTime) => {
        switch (timeType) {
            case CYCLE.MINUTE:
                return repeatTime;
            case CYCLE.HOUR:
                return repeatTime * 60;
            case CYCLE.DAY:
                return repeatTime * 1440;
            case CYCLE.WEEK:
                return repeatTime * 10080;
            case CYCLE.MONTH:
                return repeatTime * 43800;
            case CYCLE.YEAR:
                return repeatTime * 525600;
            default:
                return repeatTime;
        }
    }

    const handleDelete = async (e) => {
        e.preventDefault();
        toast.loading("삭제 중...");
        const currentDialog = dialogRef.current;  // ref에서 최신 상태 가져오기
        if (currentDialog.confirmText === 'DELETE') {
            await internalApi.delete(buildUrl('message', {messageId: rows.id})).then((res) => {
                toast.dismiss();
                toast.success(res.data.message);
                handleClose(true);
            }).catch((err) => {
                toast.dismiss();
                toast.error("삭제 실패");
                console.error(err);
            });

            handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
        } else {
            toast.error("잘못된 확인 문자입니다. DELETE 를 입력하세요.");
            handleSetDialog([{key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
        }
    }

    const handleSave = async (e) => {
        if (rows.isInvalidate()) return;

        e.preventDefault();
        toast.loading("저장 중...");
        await internalApi.post('message', {data: rows.toJson()}).then((res) => {
            toast.dismiss();
            toast.success(res.data.message);
            handleClose(true);
        }).catch((err) => {
            toast.dismiss();
            console.error(err);
            toast.error("저장 실패");
        });

        handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
    }

    const handleOptionChange = (event, newValue) => {
        let updatedOptions = newValue;

        // OS별 발송(둘 다면 ALL)
        const isAOSSelected = newValue.some(option => option.isAll && option.platform === PLATFORM.AOS);
        const isIOSSelected = newValue.some(option => option.isAll && option.platform === PLATFORM.IOS);
        if (isAOSSelected && isIOSSelected) {
            updatedOptions = [tblData[0]];
        } else if (isAOSSelected) {
            updatedOptions = newValue.filter(option => (option.isAll && option.platform === PLATFORM.AOS) || option.platform !== PLATFORM.AOS);
        } else if (isIOSSelected) {
            updatedOptions = newValue.filter(option => (option.isAll && option.platform === PLATFORM.IOS) || option.platform !== PLATFORM.IOS);
        }

        // 언어 별 발송
        const isLanguageList = updatedOptions.filter(option => option.isLanguage && option.languageOpt);
        isLanguageList.forEach(option => {
            updatedOptions = updatedOptions.filter(opt => opt.language !== option.language || opt.languageOpt);
        });

        // 국가 별 발송
        const isCountryList = updatedOptions.filter(option => option.isCountry && option.countryOpt);
        isCountryList.forEach(option => {
            updatedOptions = updatedOptions.filter(opt => (opt.country === option.country && opt.isCountry) || opt.isLanguage || opt.countryOpt);
        });

        // 전체 발송
        if (newValue.some(option => option.isAll && option.platform === PLATFORM.ALL)) {
            updatedOptions = newValue.filter(option => option.isAll && option.platform === PLATFORM.ALL);
        }

        setSelectedOptions(updatedOptions);
        handleSetRows('topic', updatedOptions.map(option => option.topic).toString());


        let newInputFields = [];

        updatedOptions.forEach(option => {
            if (option.language === 'all') {
                languageList.forEach(language => {
                    newInputFields.push({language, title: '', body: ''});
                });
            } else {
                if (newInputFields.findIndex((field) => field.language === option.language) < 0) {
                    newInputFields.push({language: option.language, title: '', body: ''});
                }
            }
        });
        setInputFields([...newInputFields]);
    };

    const handleInputChange = (index, e) => {
        const {name, value} = e.target;
        setInputFields((prevState) => {
            const newInputFields = [...prevState];
            newInputFields[index][name] = value;
            handleSetRows('body', newInputFields);
            return newInputFields;
        });
    };

    const handleSetAdText = (check) => {
        const startAdText = '(광고) ';
        const endAdText = ' (수신 거부 : 게임 내 설정)';
        setIsAdTextIncluded(check);
        const index = inputFields.findIndex((field) => field.language === 'ko');
        let body = inputFields[index].body;

        body = body.replace(startAdText, '').replace(endAdText, '');    // 초기화
        if (check) {
            body = startAdText + body + endAdText;
        }
        handleInputChange(index, {target: {name: 'body', value: body}});
    }

    return (
        <div className={"message-master-edit"} style={{ marginTop: 26}}>
            <Form layout="vertical">
                <div className={"message-master-edit-param"} style={{ display: 'flex', gap: 16 }}>
                    <Form.Item label="메시지 타입" style={{ width: 180 }}>
                        <Select
                            value={rows.messageType}
                            onChange={value => handleSetRows('messageType', value)}
                            style={{ width: '100%' }}
                        >
                            {messageTypeList.map((option) => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Memo" style={{ width: 180 }}>
                        <Input
                            value={rows.memo}
                            onChange={e => handleSetRows('memo', e.target.value)}
                        />
                    </Form.Item>
                    <Form.Item label="보내는 시간" style={{ width: 240 }}>
                        <DatePicker
                            showTime
                            value={rows.sendTime ? dayjs(rows.sendTime) : null}
                            format="YYYY-MM-DD HH:mm:ss"
                            onChange={(_, dateString) => handleSetRows('sendTime', dateString)}
                            disabled={rows.messageType === MESSAGE_TYPE.IMMEDIATE}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                </div>
                <div className={"message-master-edit-param"} style={{ display: 'flex', gap: 16 }}>
                    <Form.Item label="반복 시간 타입" style={{ width: 180 }}>
                        <Select
                            value={rows.timeType ? rows.timeType : CYCLE.MINUTE}
                            onChange={value => handleSetRepeatTime('timeType', value)}
                            disabled={rows.messageType !== MESSAGE_TYPE.REPEAT}
                            style={{ width: '100%' }}
                        >
                            {Object.keys(CYCLE).map((key) => (
                                <Select.Option key={key} value={CYCLE[key]}>
                                    {key}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="반복 시간" style={{ width: 180 }}>
                        <Input
                            value={viewRepeatTime(rows.timeType, rows.repeatTime)}
                            type="number"
                            onChange={e => handleSetRepeatTime('repeatTime', e.target.value)}
                            disabled={rows.messageType !== MESSAGE_TYPE.REPEAT}
                        />
                    </Form.Item>
                    <Form.Item label="반복 종료 시간" style={{ width: 240 }}>
                        <DatePicker
                            showTime
                            value={rows.end ? dayjs(rows.end) : null}
                            format="YYYY-MM-DD HH:mm:ss"
                            onChange={(_, dateString) => handleSetRows('end', dateString)}
                            disabled={rows.messageType !== MESSAGE_TYPE.REPEAT}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                </div>
                <Form.Item label="Topics">
                    <Select
                        mode="multiple"
                        value={selectedOptions.map(opt => opt.label)}
                        onChange={(labels) => {
                            const newOptions = tblData.filter(opt => labels.includes(opt.label));
                            handleOptionChange(null, newOptions);
                        }}
                        optionLabelProp="label"
                        style={{ width: '100%' }}
                        optionRender={option => {
                            const opt = tblData.find(o => o.label === option.label);
                            return (
                                <Checkbox checked={selectedOptions.some(sel => sel.label === option.label)}>
                                    {option.label}
                                </Checkbox>
                            );
                        }}
                    >
                        {tblData.map(option => (
                            <Select.Option key={option.label} value={option.label} label={option.label}>
                                {option.label}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <div className={"message-master-edit-body"}>
                    {inputFields.map((key, index) => (
                        <div className={"message-master-edit-body-item"} key={index}>
                            <Form.Item label={`${inputFields[index].language}_title`} style={{ marginBottom: 8 }}>
                                <Input
                                    name={'title'}
                                    value={inputFields[index].title}
                                    onChange={(e) => handleInputChange(index, e)}
                                />
                            </Form.Item>
                            <Form.Item label={`${inputFields[index].language}_body`} style={{ marginBottom: 8 }}>
                                <Input.TextArea
                                    name={'body'}
                                    value={inputFields[index].body}
                                    onChange={(e) => handleInputChange(index, e)}
                                    autoSize={{ minRows: 3 }}
                                />
                            </Form.Item>
                            {inputFields[index].language === 'ko' && (
                                <Form.Item style={{ marginBottom: 0 }}>
                                    <Checkbox
                                        checked={isAdTextIncluded}
                                        onChange={(e) => handleSetAdText(e.target.checked)}
                                    >
                                        광고 문구 추가
                                    </Checkbox>
                                </Form.Item>
                            )}
                        </div>
                    ))}
                </div>
                <div className={"message-master-buttons"}>
                    {rows.state === 0 && (
                        <SaveButton onClick={handleSave} handleSetDialog={handleSetDialog}/>
                    )}
                    <CancelButton onClick={handleClose}/>
                    <DeleteButton onClick={handleDelete} display={ rows.id ? 'flex' : 'none' } handleSetDialog={handleSetDialog}/>
                </div>
            </Form>
        </div>
    );
}

export default MessageEditModal;
